import { ResourceLanguage } from 'i18next';
import queryString from 'query-string';
import { io } from 'socket.io-client';
import { Customer } from '../../interfaces/admin-api';
import { ResultsResponse } from '../../interfaces/backend-api';
import {
    AppSetting,
    AppSettingKey,
    Components,
    Email,
    Equipment,
    EquipmentAvailabilityKPI,
    MaintenancePlan,
    MaintenanceStatus,
    Manual,
    PreventiveMaintenanceRecord,
    PreventiveMaintenanceCompletionKPI,
    ResponseTimeKPI,
    RunningHours,
    SalesforceCase,
    SalesOrder,
    Site,
    StockEquipment,
    Document,
    Bulletin,
} from '../../interfaces/customer-api';
import {
    CustomerUser,
    InfiniteDateRange,
    InternalUser,
    KPIParams,
    Language,
    UserFlags,
} from '../../interfaces/internal';
import { CaseUpdatePayload } from '../../interfaces/realtime-api';
import i18n from '../i18n';
import {
    changeOneTrustLanguage,
    dateRangeToTimeRange,
    deleteToAPI,
    logger,
    mkEnv,
    possiblyInfiniteDateRangeToTimeRange,
    postToAPI,
    putToAPI,
    queryAdminAPI,
    queryAPI,
    queryCustomerAPI,
} from '../utils';

export const maintenancePlanCache = new Map<string, MaintenancePlan[]>();

export const getCacheKey = (customerNumber: string | null, dateRange: InfiniteDateRange): string =>
    `${customerNumber}:${dateRange.startDate}`;

export const api = {
    async loadSites(customerNumber: string | null): Promise<Site[]> {
        if (customerNumber) {
            const response = await queryCustomerAPI<Site[]>(`/customers/${customerNumber}/sites`);
            if (response.status === 200) {
                return response.results;
            }
            return [];
        }
        return [];
    },
    async loadEquipment(customerNumber: string | null): Promise<Equipment[]> {
        if (customerNumber) {
            const response = await queryCustomerAPI<Equipment[]>(`/customers/${customerNumber}/equipment`);
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },
    async loadCases(
        customerNumber: string | null,
        user: CustomerUser | InternalUser | null,
    ): Promise<SalesforceCase[]> {
        if (customerNumber && user?.contactId) {
            const response = await queryCustomerAPI<SalesforceCase[]>(`/customers/${customerNumber}/cases`);
            if (response.status === 200) {
                return response.results;
            }
            return [];
        }
        return [];
    },
    async loadMaintenancePlans(
        dateRange: InfiniteDateRange,
        customerNumber: string | null,
    ): Promise<MaintenancePlan[]> {
        if (customerNumber) {
            const timeRange = possiblyInfiniteDateRangeToTimeRange(dateRange);
            const query = queryString.stringify(timeRange);
            const response = await queryCustomerAPI<MaintenancePlan[]>(
                `/customers/${customerNumber}/maintenance-plans?${query}`,
            );
            if (response.status === 200) {
                const cacheKey = getCacheKey(customerNumber, dateRange);
                maintenancePlanCache.set(cacheKey, response.results);
                return response.results;
            }
        }
        return [];
    },
    async loadEquipmentAvailabilityKPI(
        params: KPIParams,
        customerNumber: string | null,
    ): Promise<EquipmentAvailabilityKPI[]> {
        const timeRange = dateRangeToTimeRange(params);
        if (customerNumber) {
            const query = queryString.stringify({ ...timeRange, splitBy: params.splitBy });
            const response = await queryCustomerAPI<EquipmentAvailabilityKPI[]>(
                `/customers/${customerNumber}/kpi/equipment-availability?${query}`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }

        return [
            {
                average: null,
                byType: null,
                bySite: null,
                startTime: timeRange.startTime,
                endTime: timeRange.endTime,
            },
        ];
    },
    async loadPMCompletionKPI(
        params: KPIParams,
        customerNumber: string | null,
    ): Promise<PreventiveMaintenanceCompletionKPI[]> {
        const timeRange = dateRangeToTimeRange(params);
        if (customerNumber) {
            const query = queryString.stringify({ ...timeRange, splitBy: params.splitBy });

            const response = await queryCustomerAPI<PreventiveMaintenanceCompletionKPI[]>(
                `/customers/${customerNumber}/kpi/preventive-maintenance?${query}`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }

        return [
            {
                all: {
                    completed: 0,
                    planned: 0,
                    percentage: 0,
                },
                byType: {},
                bySite: {},
                startTime: timeRange.startTime,
                endTime: timeRange.endTime,
            },
        ];
    },
    async loadResponseTimeKPI(params: KPIParams, customerNumber: string | null): Promise<ResponseTimeKPI[]> {
        const timeRange = dateRangeToTimeRange(params);
        if (customerNumber) {
            const query = queryString.stringify({ ...timeRange, splitBy: params.splitBy });
            const response = await queryCustomerAPI<ResponseTimeKPI[]>(
                `/customers/${customerNumber}/kpi/response-time?${query}`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }

        return [
            {
                average_minutes: null,
                callouts: null,
                bySite: null,
                startTime: timeRange.startTime,
                endTime: timeRange.endTime,
            },
        ];
    },

    async loadSalesOrders(customerNumber: string | null): Promise<SalesOrder[]> {
        customerNumber;
        if (customerNumber) {
            const response = await queryCustomerAPI<SalesOrder[]>(`/customers/${customerNumber}/sales-orders`);
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },
    async loadComponents(customerNumber: string | null, serial_number: string | null): Promise<Components[]> {
        if (customerNumber && serial_number) {
            const response = await queryCustomerAPI<Components[]>(
                `/customers/${customerNumber}/equipment/${serial_number}/components`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },

    async loadRunningHours(customerNumber: string | null, serial_number: string): Promise<RunningHours> {
        if (customerNumber) {
            const response = await queryCustomerAPI<RunningHours>(
                `/customers/${customerNumber}/equipment/${serial_number}/running-hours`,
            );

            if (response.status === 200) {
                return response.results;
            }
        }
        return { runningHours: undefined };
    },

    async loadStockEquipments(): Promise<StockEquipment[]> {
        const response = await queryCustomerAPI<StockEquipment[]>(`/stock-equipment`);
        if (response.status === 200) {
            return response.results;
        }
        return [];
    },

    async loadStockEquipmentById(id: number): Promise<StockEquipment | null> {
        const response = await queryCustomerAPI<StockEquipment>(`/stock-equipment/${id}`);
        if (response.status === 200) {
            return response.results;
        }
        return null;
    },

    async insertStockEquipment(details: StockEquipment): Promise<string | null> {
        try {
            const response = await postToAPI<StockEquipment, ResultsResponse<string>>(
                `/admin/stock-equipment/`,
                details,
            );
            if (response && response.status === 200) {
                logger.info(`stock equipment details updated successfully: ${response.results}`);
                return response.results;
            }
        } catch (error) {
            logger.error('Failed to insert stock equipment:', error);
        }
        return null;
    },

    async updateStockEquipment(id: number, details: Partial<StockEquipment>): Promise<StockEquipment | null> {
        try {
            const response = await putToAPI<Partial<StockEquipment>, ResultsResponse<StockEquipment>>(
                `/admin/stock-equipment/${id}`,
                details,
            );
            if (response && response.status === 200) {
                logger.info(`Stock equipment updated successfully: ${response.results}`);
                return response.results;
            }
        } catch (error) {
            logger.error('Failed to update stock equipment:', error);
        }
        return null;
    },

    async deleteStockEquipment(id: number): Promise<boolean> {
        try {
            const response = await deleteToAPI<undefined, ResultsResponse<string>>(
                `/admin/stock-equipment/${id}`,
                undefined,
            );
            if (response && response.status === 200) {
                logger.info(`Stock equipment with ID ${id} deleted successfully`);
                return true;
            }
        } catch (error) {
            logger.error('Failed to delete stock equipment:', error);
        }
        return false;
    },

    async getPresignedUrls(fileNames: string[], folder?: string): Promise<{ uploadUrl: string; key: string }[] | null> {
        try {
            const folderParam = folder ? `&folder=${encodeURIComponent(folder)}` : '';
            const response = await queryAdminAPI<{ uploadUrl: string; key: string }[]>(
                `/admin/document/presigned-urls?fileNames=${fileNames.join(',')}${folderParam}`,
            );
            if (response && response.status === 200) {
                logger.info(`Got upload presigned URLs: ${response.results.length}`);
                return response.results;
            }
        } catch (error) {
            logger.error('Failed to get upload presigned URLs:', error);
        }
        return null;
    },

    async uploadFileToS3(file: File, uploadUrl: string): Promise<boolean> {
        try {
            const response = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });
            logger.info('S3 upload response:', response);
            if (response.ok) {
                logger.info(`File ${file.name} uploaded successfully to S3`);
                return true;
            } else {
                logger.error(`Failed to upload file ${file.name} to S3:`, response.statusText);
                return false;
            }
        } catch (error) {
            logger.error(`Error uploading file ${file.name} to S3:`, error);
            return false;
        }
    },

    async createDocuments(documents: Document[]): Promise<number[] | null> {
        try {
            const response = await postToAPI<Document[], ResultsResponse<number[]>>(`/admin/document`, documents);
            if (response && response.status === 200) {
                logger.info(`Documents created successfully: ${response.results}`);
                return response.results;
            }
        } catch (error) {
            logger.error('Failed to create documents:', error);
        }
        return null;
    },

    async getDocuments(documentKeys: string[]): Promise<Document[]> {
        try {
            const response = await queryCustomerAPI<Document[]>(`/document?keys=${documentKeys.join(',')}`);
            if (response && response.status === 200) {
                return response.results;
            }
        } catch (error) {
            logger.error('Failed to get documents:', error);
        }
        return [];
    },

    async deleteDocument(documentKeys: string): Promise<boolean> {
        try {
            const response = await deleteToAPI<undefined, ResultsResponse<string>>(
                `/admin/document?keys=${documentKeys}`,
                undefined,
            );

            if (response && response.status === 200) {
                logger.info(`Docuemnts with IDs ${documentKeys} deleted successfully`);
                return true;
            }
        } catch (error) {
            logger.error('Failed to delete stock equipment:', error);
        }
        return false;
    },

    async loadMaintenancePlansForEquipment(
        customerNumber: string | null,
        equipmentId: string | null,
    ): Promise<MaintenancePlan[]> {
        if (customerNumber && equipmentId) {
            const response = await queryCustomerAPI<MaintenancePlan[]>(
                `/customers/${customerNumber}/equipment/${equipmentId}/maintenance-plans`,
            );
            if (response.status === 200) {
                const nonDeclinedPlans = response.results.filter((plan) => plan.status !== MaintenanceStatus.DECLINED);
                return nonDeclinedPlans;
            }
        }
        return [];
    },

    async getCustomer(customerNumber: string): Promise<Customer | null> {
        const response = await queryAdminAPI<Customer>(`/admin/customers/${customerNumber}`);
        if (response?.status === 200) {
            return response.results;
        }
        return null;
    },

    async loadInsightUsers(customerNumber: string): Promise<number | null> {
        const response = await queryAdminAPI<{ userCount: number }>(`/customers/${customerNumber}/insight-user-count`);
        if (response?.status === 200) {
            return response.results.userCount;
        }
        return null;
    },

    async getSessionUser(): Promise<CustomerUser | InternalUser | null> {
        const response = await queryAPI<ResultsResponse<CustomerUser | InternalUser>>(`/user`);
        if (response.status === 200) {
            return response.results;
        }
        return null;
    },

    async loadManuals(customerNumber: string | null, equipment_number: string): Promise<Manual[]> {
        if (customerNumber && equipment_number) {
            const response = await queryCustomerAPI<Manual[]>(
                `/customers/${customerNumber}/equipment/${equipment_number}/manuals`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },

     async getBulletins(customerNumber: string | null): Promise<Bulletin[]> {
        if (customerNumber) {
            const response = await queryCustomerAPI<Bulletin[]>(
                `/customers/${customerNumber}/bulletins`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },

    async loadPMRecords(customerNumber: string | null, equipmentNumber: string | null): Promise<PreventiveMaintenanceRecord[]> {
        if (customerNumber && equipmentNumber) {
            const response = await queryCustomerAPI<PreventiveMaintenanceRecord[]>(
                `/customers/${customerNumber}/equipment/${equipmentNumber}/preventive-maintenance`,
            );
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },

    async loadPMReportLink(customerNumber: string, equipmentNumber: string, soNumber: string): Promise<string | null> {
        const response = await queryCustomerAPI<{ downloadUrl: string }>(
            `/customers/${customerNumber}/equipment/${equipmentNumber}/preventive-maintenance/${soNumber}/report`,
        );
        if (response.status === 200) {
            return response.results.downloadUrl;
        }
        return null;
    },

    async getManualsBySerialNumber(serialNumber: string): Promise<Manual[]> {
        if (serialNumber) {
            const response = await queryCustomerAPI<Manual[]>(`/extadmin/equipment/${serialNumber}/manuals`);
            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },

    async closeSession(): Promise<void> {
        await queryAPI<void>('/logout/close-session');
    },

    async updateUserFlags(flags: Partial<UserFlags>): Promise<UserFlags> {
        return postToAPI<Partial<UserFlags>, UserFlags>('/user/flags', flags);
    },

    async updateAppUser(user: InternalUser | CustomerUser): Promise<void> {
        // No body because the session user is posted along with this call that is stored in db
        putToAPI<Partial<InternalUser | CustomerUser>, InternalUser | CustomerUser>('/user', user);
    },

    subscribeToCaseUpdates(caseIds: string[], onPublish: (payload: CaseUpdatePayload) => void): void {
        if (!caseIds.length) return;

        const socket = io(`${mkEnv.apiBaseURL}/cases`, {
            rememberUpgrade: true,
            transports: ['websocket', 'polling'],
        });
        socket.on('connect', () => {
            // wait for few seconds, allow server to setup the subscribe topic
            setTimeout(() => {
                socket.emit('subscribe', caseIds);
            }, 4000);
        });
        socket.on('publish', onPublish);
    },

    async getEmails(customerNumber: string, requestType: string): Promise<Email[]> {
        if (customerNumber) {
            const response = await queryCustomerAPI<Email[]>(
                `/customers/${customerNumber}/emails?emailType=${requestType}`,
            );

            if (response.status === 200) {
                return response.results;
            }
        }
        return [];
    },

    async updateEmail(customerNumber: string, emailId: string, details: Partial<Email>) {
        try {
            if (customerNumber) {
                const response = await putToAPI<Partial<Email>, ResultsResponse<Email>>(
                    `/customers/${customerNumber}/emails/${emailId}`,
                    details,
                );
                if (response && response.status === 200) {
                    return response.results;
                }
            }
        } catch (error) {
            logger.error('Failed to update email:', error);
        }
        return null;
    },

    async insertEmail(customerNumber: string, details: Email): Promise<string | null> {
        try {
            if (customerNumber) {
                const response = await postToAPI<Email, ResultsResponse<string>>(
                    `/customers/${customerNumber}/email/`,
                    details,
                );
                if (response && response.status === 200) {
                    logger.info(`Maintenance email details updated successfully: ${response.results}`);
                    return response.results;
                }
            }
        } catch (error) {
            logger.error('Failed to insert maintenance email:', error);
        }
        return null;
    },

    async getAppSettings(keys: AppSettingKey[]): Promise<AppSetting[] | null> {
        const response = await queryCustomerAPI<AppSetting[]>(`/customers/app-settings?keys=${keys.join(',')}`);
        if (response?.status === 200) {
            return response.results;
        }
        return null;
    },

    async upsertAppSetting(setting: AppSetting): Promise<string | null> {
        try {
            const response = await postToAPI<AppSetting, ResultsResponse<string>>(`/admin/app-settings`, setting);
            if (response && response.status === 200) {
                logger.info(`Maintenance email details updated successfully: ${response.results}`);
                return response.results;
            }
        } catch (error) {
            logger.error('Failed to upsert app setting:', error);
        }
        return null;
    },
};

const getLocalStorageLanguageKey = (username: string) => `language-${username}`;

function getLanguageImportFunc(lang: Language) {
    switch (lang) {
        case 'es':
            return () => import(`../translations/es`);
        case 'de':
            return () => import(`../translations/de`);
        case 'fr':
            return () => import(`../translations/fr`);
        case 'zh':
            return () => import(`../translations/zh`);
        default:
            return () => import(`../translations/en`);
    }
}

export const sideEffects = {
    async setLanguage(username: string, lang: Language): Promise<void> {
        try {
            const importFunc = getLanguageImportFunc(lang);

            const imported = await importFunc();

            const resource = imported.default as ResourceLanguage;
            Object.entries(resource).map(([namespace, translations]) => {
                i18n.addResourceBundle(lang, namespace, translations);
            });

            await i18n.changeLanguage(lang);

            if (localStorage) {
                localStorage.setItem(getLocalStorageLanguageKey(username), lang);
            }

            changeOneTrustLanguage(lang);
        } catch (error) {
            logger.error(`Importing translation file for language "${lang}" failed`);
        }
    },
    getLanguage(username: string): Language | null {
        if (!localStorage) {
            return null;
        }
        return localStorage.getItem(getLocalStorageLanguageKey(username)) as Language | null;
    },
};
